import { useRef, useState } from 'react';
import { useFab } from '../contexts/FabContext';
import { mikrofonAnfragen, zeigeMikrofonFehler } from '../lib/mikrofon';

const CODEC_PREF = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
  'audio/wav',
];

function getMimeExt(): { mime: string; ext: string } {
  const mime = CODEC_PREF.find(m => MediaRecorder.isTypeSupported(m)) ?? '';
  const ext  = mime.includes('ogg') ? 'ogg'
             : mime.includes('mp4') ? 'mp4'
             : mime.includes('wav') ? 'wav'
             : 'webm';
  return { mime, ext };
}

export function useDiktatRecording() {
  const { setIsRecording } = useFab();
  const [aufnahmePhase, setAufnahmePhase] = useState<'idle' | 'aufnahme'>('idle');
  const [dauer, setDauer]   = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef   = useRef<Blob[]>([]);
  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const resolveRef  = useRef<((v: { blob: Blob; ext: string }) => void) | null>(null);
  const extRef      = useRef('webm');

  async function startRecording(preAcquiredStream?: Promise<MediaStream> | null): Promise<void> {
    chunksRef.current = [];
    setDauer(0);

    let stream: MediaStream;
    try {
      stream = preAcquiredStream
        ? await preAcquiredStream
        : await mikrofonAnfragen();
    } catch (err) {
      // Welle H6: Hinweis mit Anleitung zeigen; kurzer Titel bleibt die Fehlerzeile im Dialog
      throw new Error(zeigeMikrofonFehler(err));
    }

    const { mime, ext } = getMimeExt();
    extRef.current = ext;

    let recorder: MediaRecorder;
    try {
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach(t => t.stop());
      throw new Error('MediaRecorder konnte nicht gestartet werden.');
    }

    recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };

    recorder.onstop = () => {
      stream.getTracks().forEach(t => t.stop());
      setIsRecording(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setAufnahmePhase('idle');
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mime });
      resolveRef.current?.({ blob, ext: extRef.current });
      resolveRef.current = null;
    };

    recorderRef.current = recorder;
    recorder.start();
    setAufnahmePhase('aufnahme');
    setIsRecording(true);
    timerRef.current = setInterval(() => setDauer(d => d + 1), 1000);
  }

  function stopRecording(): Promise<{ blob: Blob; ext: string }> {
    return new Promise((resolve, reject) => {
      const rec = recorderRef.current;
      if (!rec || rec.state === 'inactive') {
        reject(new Error('Kein aktiver Recorder.'));
        return;
      }
      resolveRef.current = resolve;
      rec.stop();
    });
  }

  function cleanup() {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setIsRecording(false);
    setAufnahmePhase('idle');
  }

  return { aufnahmePhase, dauer, startRecording, stopRecording, cleanup };
}
